import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, Play } from "lucide-react";

// Project data
const projectsData = {
  'jiberish-lookbooks': {
    title: 'Jiberish Lookbooks',
    category: 'Fashion',
    description: 'Video content for Jiberish, a Denver-based streetwear brand known for its ski culture roots and urban aesthetic.',
    heroImage: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1741295161790-F80EGTCXO4CNYLIJA2WK/JiberishxSmartwool_aldenbonecutter.jpg?format=2500w',
    videos: [
      { type: 'vimeo', id: '995607403', title: 'Jiberish July 2024 Warehouse Sale' },
      { type: 'vimeo', id: '995606890', title: 'Jiberish June 2024 Lookbook' },
      { type: 'vimeo', id: '742033138', title: 'Jiberish February 2022 Lookbook' },
      { type: 'vimeo', id: '742033356', title: 'Jiberish Hockey Team 2022' },
      { type: 'vimeo', id: '742033616', title: 'Jiberish June 2022 Lookbook' },
      { type: 'vimeo', id: '742033724', title: 'Jiberish March 2022 Lookbook' },
      { type: 'vimeo', id: '742034406', title: 'Jiberish May 2022 Lookbook' },
      { type: 'vimeo', id: '742036178', title: 'Jiberish Revival Collection' }
    ],
    gallery: []
  },
  'strictly': {
    title: 'Strictly',
    category: 'Ski Film',
    description: 'Ski film series documenting the raw energy and creative expression of freeskiing culture. A story-first approach capturing momentum, character, and narrative on the mountain.',
    heroImage: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1741295227717-92AFCPYBHFAD25BYZHWS/Screenshot+2024-08-07+at+3.54.38%E2%80%AFPM.png?format=2500w',
    videos: [
      { type: 'vimeo', id: '303668864', title: 'Strictly Business' },
      { type: 'youtube', id: 'gcyb4ydUlRs', title: 'Most Gutter' }
    ],
    gallery: [
      {
        image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1722992610719-95LPJN7AHHMJRHLA1GQG/SB+Cover.png?format=1500w'
      },
      {
        image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1723082210032-60JWFKTOWH378FAA4WSW/MG+Cover.png?format=1500w'
      },
      {
        image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1740980587016-PCSJFTL8XUA9L5NFJISI/Screen%2BShot%2B2019-08-21%2Bat%2B1.20.58%2BPM.png?format=1500w'
      },
      {
        image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1740980579521-UEKX3H0X538ET9FRKKW8/Screen%2BShot%2B2019-08-21%2Bat%2B1.21.10%2BPM.png?format=1500w'
      },
      {
        image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1740980571789-0FARHEVM4C6U3Z7KGW6B/Screen%2BShot%2B2019-08-21%2Bat%2B1.20.46%2BPM.png?format=1500w'
      },
      {
        image: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1740980564444-HCZK8F5B5SYGN00ROB0Z/Screen%2BShot%2B2019-08-21%2Bat%2B1.21.55%2BPM.png?format=1500w'
      }
    ]
  },
  'oliver-hawaii': {
    title: 'Oliver Hawaii',
    category: 'Documentary',
    description: "A mini documentary exploring the heart of small business in Kailua, Hawaii. This film captures what it truly takes to stay in business in today's ever-changing landscape\u2014the passion, the challenges, and the community spirit that keeps local entrepreneurs going.",
    heroImage: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1767742565984-CUL1DAJOP5YP03LDV3YQ/Oliver%2BHeader.png?format=2500w',
    videos: [
      { type: 'youtube', id: '9fCYks143EI', title: 'Oliver Hawaii' }
    ],
    gallery: []
  },
  'fusion': {
    title: 'Fusion Specialties',
    category: 'Commercial',
    description: 'Multimedia projects for Fusion Specialties showcasing the full capabilities of the company as well as a new sustainable collection rollout: BOTANIQ.',
    heroImage: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1765992420635-JM2T2DJY997DS7F0Q0MB/Botanic+Wide+Cover.png?format=2500w',
    videos: [
      { type: 'youtube', id: 'ejIy7qJRk3o', title: 'Fusion Specialties Overview' },
      { type: 'youtube', id: 'ukLrh8W4gpg', title: 'BOTANIQ: A New Face in Sustainability', featured: true },
      { type: 'youtube', id: 'bqufIEV1_0U', title: 'BOTANIQ Teaser 1' },
      { type: 'youtube', id: 'cM4ri6NVZ9k', title: 'BOTANIQ Teaser 2' },
      { type: 'youtube', id: 'xqv-AxZtAJA', title: 'BOTANIQ Documentary' }
    ],
    gallery: []
  },
  'social-media': {
    title: 'Social Media',
    category: 'Content',
    description: 'Social-first content creation for brands across fashion, sports, and lifestyle. High-speed editorial cuts to lo-fi UGC-style vertical video built for real performance.',
    heroImage: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1741295068570-EKY0HV2MSH56BYT7TG5I/Socials+Export+2.png?format=2500w',
    verticalVideos: [
      { type: 'youtube-short', id: 'isGRsqY-Et4', title: 'Breezy Spring 2026' },
      { type: 'youtube-short', id: 'W-9Ls4AHmXo', title: "Arby's x Bob Does Sports" },
      { type: 'youtube-short', id: 'l88DUPohce4', title: 'Takomo x Bob Does Sports' },
      { type: 'instagram', url: 'https://www.instagram.com/reel/C_5364Buzos/', title: 'Puppet Theater SS25', thumbnail: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/6c8d0fd6-e7dd-44f0-a0de-d170e7142274/Puppet+Theater.png?format=500w' },
      { type: 'instagram', url: 'https://www.instagram.com/reel/C9nKb0hPK8j/', title: 'Breezy Open Promo', thumbnail: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/97307524-1a90-475c-9164-5a00af428036/Breezy.png?format=500w' },
      { type: 'instagram', url: 'https://www.instagram.com/reel/CrRrnXzg-SJ/', title: 'Karu x Steadbook', thumbnail: 'https://images.unsplash.com/photo-1593139907093-be3da243761b?crop=entropy&cs=srgb&fm=jpg&w=500&q=85' },
      { type: 'vimeo-vertical', id: '853118122', title: 'Human Made SS23' },
      { type: 'instagram', url: 'https://www.instagram.com/p/CwVYqKYuqvL/', title: 'Homme Issey Miyake SS23', thumbnail: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1691692850898-V2G9AXRF5TBEYLEXPTKZ/Screen+Shot+2023-08-10+at+12.40.39+PM.png?format=500w' },
      { type: 'instagram', url: 'https://www.instagram.com/p/Cq7tDWVuPKZ/', title: 'Stone Island SS23', thumbnail: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1722989783908-4FFL2EFNB8OW90PLMIDG/SI+thumb.jpg?format=500w' }
    ],
    videos: [],
    gallery: []
  },
  'jpv-reel': {
    title: 'JPV Reel',
    category: 'Showreel',
    description: 'A curated collection of the best work from Jack Pepper Visuals showcasing expertise in video production, cinematography, and visual storytelling.',
    heroImage: 'https://images.squarespace-cdn.com/content/v1/5d582c1368921200016443ad/1723487821663-R218MCAIJREOURSTAGQJ/JPV+Cover+.png?format=2500w',
    videos: [
      { type: 'vimeo', id: '755279690', title: 'JPV Reel 2024' }
    ],
    gallery: []
  }
};

// Navigation order for prev/next
const projectOrder = ['jpv-reel', 'jiberish-lookbooks', 'strictly', 'oliver-hawaii', 'fusion', 'social-media'];

// Shared animation props
const fadeIn = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 } };
const fadeInSlow = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const staggerDelay = (index) => ({ delay: 0.2 + index * 0.08 });

// Video embed component
const VideoEmbed = ({ video, featured = false }) => {
  const src = video.type === 'vimeo' || video.type === 'vimeo-vertical'
    ? `https://player.vimeo.com/video/${video.id}?title=0&byline=0&portrait=0`
    : `https://www.youtube.com/embed/${video.id}`;
  
  return (
    <div className={featured ? 'mb-12' : ''}>
      <div className="relative pb-[56.25%] h-0 overflow-hidden">
        <iframe
          src={src}
          className="absolute top-0 left-0 w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          title={video.title}
        />
      </div>
      <p className="mt-3 font-mono text-sm text-zinc-500 uppercase tracking-wider">
        {video.title}
        {video.featured && <span className="ml-2 text-[#FFB703]">Featured</span>}
      </p>
    </div>
  );
};

// Vertical video card for social content
const VerticalVideoCard = ({ video, index }) => {
  const transition = staggerDelay(index);

  if (video.type === 'youtube-short') {
    return (
      <motion.div {...fadeIn} transition={transition} className="group">
        <div className="relative overflow-hidden bg-[#0A0A0A] border border-white/10">
          <div className="relative pb-[177.78%] h-0 overflow-hidden">
            <iframe
              src={`https://www.youtube.com/embed/${video.id}`}
              className="absolute top-0 left-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={video.title}
            />
          </div>
        </div>
        <p className="mt-3 font-mono text-xs text-zinc-500 uppercase tracking-wider">{video.title}</p>
      </motion.div>
    );
  }

  if (video.type === 'vimeo-vertical') {
    return (
      <motion.div {...fadeIn} transition={transition} className="group">
        <div className="relative overflow-hidden bg-[#0A0A0A] border border-white/10">
          <div className="relative pb-[177.78%] h-0 overflow-hidden">
            <iframe
              src={`https://player.vimeo.com/video/${video.id}?title=0&byline=0&portrait=0`}
              className="absolute top-0 left-0 w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={video.title}
            />
          </div>
        </div>
        <p className="mt-3 font-mono text-xs text-zinc-500 uppercase tracking-wider">{video.title}</p>
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeIn} transition={transition}>
      <a href={video.url} target="_blank" rel="noopener noreferrer" className="group block" data-testid={`social-video-${index}`}>
        <div className="relative overflow-hidden bg-[#0A0A0A] border border-white/10 aspect-[9/16]">
          <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#FFB703]/80 transition-colors duration-300">
              <Play size={24} className="text-white ml-1" />
            </div>
          </div>
          <div className="absolute bottom-3 left-3 right-3">
            <div className="flex items-center gap-2">
              <ExternalLink size={12} className="text-white/70" />
              <span className="text-xs text-white/70">Instagram</span>
            </div>
          </div>
        </div>
        <p className="mt-3 font-mono text-xs text-zinc-500 uppercase tracking-wider group-hover:text-[#FFB703] transition-colors">{video.title}</p>
      </a>
    </motion.div>
  );
};

// Project page navigation bar
const ProjectNav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center justify-between h-20">
        <Link 
          to="/"
          data-testid="back-home"
          className="flex items-center gap-2 text-zinc-400 hover:text-[#FFB703] transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-mono text-sm uppercase tracking-wider">Back</span>
        </Link>
        <Link 
          to="/"
          className="font-display text-2xl tracking-tight text-white hover:text-[#FFB703] transition-colors"
        >
          JACK PEPPER
        </Link>
        <div className="w-20" />
      </div>
    </div>
  </nav>
);

// Project hero section
const ProjectHero = ({ project }) => (
  <section className="relative pt-20">
    <div className="relative h-[60vh] md:h-[70vh]">
      <img src={project.heroImage} alt={project.title} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        <motion.p {...fadeInSlow} className="font-mono text-xs uppercase tracking-[0.2em] text-[#FFB703] mb-4">
          {project.category}
        </motion.p>
        <motion.h1 {...fadeIn} transition={{ delay: 0.1 }} className="font-display text-4xl md:text-6xl lg:text-7xl uppercase tracking-tight">
          {project.title}
        </motion.h1>
      </div>
    </div>
  </section>
);

// Videos list (16:9)
const VideosList = ({ videos }) => (
  <div className="space-y-12">
    {videos.map((video) => (
      <motion.div key={video.id} {...fadeIn} transition={{ delay: 0.3 }}>
        <VideoEmbed video={video} featured={video.featured} />
      </motion.div>
    ))}
  </div>
);

// Vertical videos grid (9:16)
const VerticalVideosGrid = ({ videos }) => (
  <div>
    <motion.h2 {...fadeInSlow} transition={{ delay: 0.25 }} className="font-display text-2xl md:text-3xl uppercase tracking-tight mb-8">
      Social Content
    </motion.h2>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
      {videos.map((video, index) => (
        <VerticalVideoCard key={video.id || video.url || video.title} video={video} index={index} />
      ))}
    </div>
  </div>
);

// Image gallery grid
const ImageGallery = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-16">
    {items.map((item, index) => (
      <motion.div key={item.image} {...fadeIn} transition={{ delay: 0.3 + index * 0.1 }} className="group">
        <div className="relative overflow-hidden">
          <img src={item.image} alt={item.title || `Gallery ${index + 1}`} className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-500" />
        </div>
        {item.title && <p className="mt-4 font-mono text-sm text-zinc-500 uppercase tracking-wider">{item.title}</p>}
      </motion.div>
    ))}
  </div>
);

// Prev/Next project navigation
const ProjectPagination = ({ prevProject, nextProject }) => (
  <section className="border-t border-white/10 py-12">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center justify-between">
        {prevProject ? (
          <Link to={`/project/${prevProject}`} data-testid="prev-project" className="group flex items-center gap-3 text-zinc-400 hover:text-[#FFB703] transition-colors">
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <div>
              <p className="font-mono text-xs uppercase tracking-wider mb-1">Previous</p>
              <p className="font-display text-lg uppercase">{projectsData[prevProject]?.title}</p>
            </div>
          </Link>
        ) : <div />}
        {nextProject ? (
          <Link to={`/project/${nextProject}`} data-testid="next-project" className="group flex items-center gap-3 text-zinc-400 hover:text-[#FFB703] transition-colors text-right">
            <div>
              <p className="font-mono text-xs uppercase tracking-wider mb-1">Next</p>
              <p className="font-display text-lg uppercase">{projectsData[nextProject]?.title}</p>
            </div>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        ) : <div />}
      </div>
    </div>
  </section>
);

// Footer CTA
const ProjectFooterCTA = () => (
  <section className="py-16 border-t border-white/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
      <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight mb-6">
        Like what you see?
      </h2>
      <Link to="/#contact" data-testid="project-contact-cta" className="inline-block px-8 py-4 bg-[#FFB703] text-black font-bold uppercase tracking-wider hover:bg-[#EAA015] transition-colors">
        Let's Work Together
      </Link>
    </div>
  </section>
);

const ProjectPage = () => {
  const { slug } = useParams();
  const project = projectsData[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-white mb-4">Project Not Found</h1>
          <Link to="/" className="text-[#FFB703] hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const currentIndex = projectOrder.indexOf(slug);
  const prevProject = currentIndex > 0 ? projectOrder[currentIndex - 1] : null;
  const nextProject = currentIndex < projectOrder.length - 1 ? projectOrder[currentIndex + 1] : null;

  const hasVideos = project.videos && project.videos.length > 0;
  const hasVerticalVideos = project.verticalVideos && project.verticalVideos.length > 0;
  const hasGallery = project.gallery && project.gallery.length > 0;

  return (
    <div className="min-h-screen bg-black">
      <ProjectNav />
      <ProjectHero project={project} />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p {...fadeInSlow} transition={{ delay: 0.2 }} className="text-xl md:text-2xl text-zinc-300 leading-relaxed max-w-3xl mb-16">
            {project.description}
          </motion.p>

          {hasVideos && <VideosList videos={project.videos} />}
          {hasVerticalVideos && <VerticalVideosGrid videos={project.verticalVideos} />}
          {hasGallery && <ImageGallery items={project.gallery} />}
        </div>
      </section>

      <ProjectPagination prevProject={prevProject} nextProject={nextProject} />
      <ProjectFooterCTA />
    </div>
  );
};

export default ProjectPage;
